import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { streamChat } from "@/lib/api";

const SUGGESTIONS = [
  "Is the class free?",
  "Do I need a nursing background?",
  "What are the 3 payer pathways?",
];

const uid = () => Math.random().toString(36).slice(2);

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm Tasha's Assistant. Ask me anything about the free class series or starting your home health care agency. \uD83D\uDC4B",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);

    await streamChat(
      sessionId,
      msg,
      (delta) =>
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content: copy[copy.length - 1].content + delta,
          };
          return copy;
        }),
      () => setBusy(false),
      () => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              copy[copy.length - 1].content ||
              "Sorry, I had trouble responding. Please try again or email contact@gotoasktasha.com.",
          };
          return copy;
        });
        setBusy(false);
      }
    );
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-gold text-ink shadow-lg shadow-gold/20"
        aria-label="Open chat assistant"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chatbot-panel"
            className="fixed bottom-24 right-6 z-[70] flex h-[70vh] max-h-[560px] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-gold/30 bg-ink-elevated shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-cream/10 bg-ink px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15 text-gold">
                <Sparkles size={18} />
              </div>
              <div>
                <p className="font-serif text-lg font-semibold leading-none text-cream">Tasha's Assistant</p>
                <p className="mt-1 font-sans text-xs text-cream/50">Ask about the class · #AskTasha</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-3 font-sans text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-gold text-ink"
                        : "rounded-bl-sm border border-cream/10 bg-ink text-cream/85"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <TypingDots /> : "")}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid="chat-suggestion"
                      className="rounded-full border border-gold/30 px-3 py-1.5 font-sans text-xs text-gold transition-colors hover:bg-gold/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-cream/10 bg-ink p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="chat-input"
                placeholder="Type your question…"
                className="flex-1 rounded-full border border-cream/15 bg-ink-elevated px-4 py-2.5 font-sans text-sm text-cream placeholder:text-cream/30 outline-none focus:border-gold/50"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="chat-send-btn"
                className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-gold text-ink transition-opacity disabled:opacity-40"
              >
                <Send size={17} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const TypingDots = () => (
  <span className="flex gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-gold"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ repeat: Infinity, duration: 1, delay: i * 0.2 }}
      />
    ))}
  </span>
);

export default Chatbot;
